import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, addDoc, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";

const SIZES = ["CXS", "CS", "CM", "CL", "CXL", "AXS", "AS", "AM", "AL", "AXL"];
const LEVELS = [4, 5, 6, 7, 8, 9, 10];
const COLLECTION = "azbga_uniform_sizes";

const UniformSizes = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    athleteName: "",
    level: "",
    gym: "",
    personCompletingForm: "",
    jerseySize: "",
    competitionShortsSize: "",
    pommelPantsSize: "",
    longSleeveAllStarShirt: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const sortedSubmissions = useMemo(() => {
    return [...submissions].sort((a, b) => {
      const gymCompare = (a.gym || "").localeCompare(b.gym || "");
      if (gymCompare !== 0) return gymCompare;
      return (a.athleteName || "").localeCompare(b.athleteName || "");
    });
  }, [submissions]);

  const fetchSubmissions = useCallback(async () => {
    setLoadingSubmissions(true);
    try {
      const snapshot = await getDocs(collection(db, COLLECTION));
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setSubmissions(list);
    } catch (err) {
      console.error("Error loading uniform size submissions:", err);
    } finally {
      setLoadingSubmissions(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.athleteName.trim()) {
      setError("Athlete name is required.");
      return;
    }
    if (!formData.level) {
      setError("Level is required.");
      return;
    }
    if (!formData.gym.trim()) {
      setError("Gym is required.");
      return;
    }
    if (!formData.personCompletingForm.trim()) {
      setError("Person completing form is required.");
      return;
    }
    if (!formData.jerseySize || !formData.competitionShortsSize || !formData.pommelPantsSize || !formData.longSleeveAllStarShirt) {
      setError("All size fields are required.");
      return;
    }

    try {
      setSubmitting(true);
      await addDoc(collection(db, COLLECTION), {
        ...formData,
        level: Number(formData.level),
        createdAt: new Date(),
        submittedByUid: user?.uid || null
      });
      setSuccess("Submission received. Thank you!");
      setFormData({
        athleteName: "",
        level: "",
        gym: "",
        personCompletingForm: "",
        jerseySize: "",
        competitionShortsSize: "",
        pommelPantsSize: "",
        longSleeveAllStarShirt: ""
      });
      fetchSubmissions();
    } catch (err) {
      console.error("Error submitting uniform sizes:", err);
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Arizona All-Star Uniform Sizes</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Please submit sizes for your athletes to help us prepare for distributing All-Star uniforms at States and the All-Star Clinic.
              If you only wish to submit sizes for athletes that will most likely qualify for the All-Star team, that would be OK.
              You are welcome to submit sizes for all of your athletes if you&apos;d prefer.
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              When you submit this form, it will not automatically collect your details like name and email address unless you provide it yourself.
            </p>
          </div>
          <Link
            to="/admin"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
          >
            Back to Dashboard
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Athlete Name *</label>
              <input
                type="text"
                name="athleteName"
                value={formData.athleteName}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                placeholder="Enter athlete name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Level *</label>
              <select
                name="level"
                value={formData.level}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="">Select level</option>
                {LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Gym *</label>
              <input
                type="text"
                name="gym"
                value={formData.gym}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                placeholder="Enter gym name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Person Completing Form *</label>
              <input
                type="text"
                name="personCompletingForm"
                value={formData.personCompletingForm}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                placeholder="Enter your name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Jersey Size *</label>
                <select
                  name="jerseySize"
                  value={formData.jerseySize}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                >
                  <option value="">Select size</option>
                  {SIZES.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Competition Shorts Size *</label>
                <select
                  name="competitionShortsSize"
                  value={formData.competitionShortsSize}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                >
                  <option value="">Select size</option>
                  {SIZES.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Pommel Pants Size *</label>
                <select
                  name="pommelPantsSize"
                  value={formData.pommelPantsSize}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                >
                  <option value="">Select size</option>
                  {SIZES.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Long Sleeve All Star Shirt *</label>
                <select
                  name="longSleeveAllStarShirt"
                  value={formData.longSleeveAllStarShirt}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                >
                  <option value="">Select size</option>
                  {SIZES.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Submitted Sizes</h2>
          {loadingSubmissions ? (
            <p className="text-gray-600 dark:text-gray-300">Loading submissions...</p>
          ) : sortedSubmissions.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-300">No submissions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Gym</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Athlete</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">Level</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Person Completing Form</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Parent Email</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">Jersey</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">Shorts</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">Pommel Pants</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">Long Sleeve</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{submission.gym}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{submission.athleteName}</td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-gray-100">{submission.level}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{submission.personCompletingForm}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{submission.parentEmail}</td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-gray-100">{submission.jerseySize}</td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-gray-100">{submission.competitionShortsSize}</td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-gray-100">{submission.pommelPantsSize}</td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-gray-100">{submission.longSleeveAllStarShirt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UniformSizes;
